import { useEffect, useState, useRef } from "react";
import { Search as SearchIcon, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import api, { resolveImageUrl } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import VerifiedBadge from "@/components/VerifiedBadge";

function initials(name = "?") { return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase(); }

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState({ scholars: [], gurus: [], posts: [], jobs: [], events: [] });
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const debounce = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    clearTimeout(debounce.current);
    if (!q.trim()) { setResults({ scholars: [], gurus: [], posts: [], jobs: [], events: [] }); return; }
    debounce.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/search", { params: { q: q.trim() } });
        setResults(data); setOpen(true);
      } catch {} finally { setLoading(false); }
    }, 250);
  }, [q]);

  const totalCount = ["scholars", "gurus", "posts", "jobs", "events"]
    .reduce((s, k) => s + (results[k]?.length || 0), 0);

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };

  return (
    <div ref={wrapRef} className="relative flex-1 max-w-md">
      <div className="relative">
        <SearchIcon className="absolute w-4 h-4 top-1/2 left-3 -translate-y-1/2 text-[color:var(--ayur-mute)]" />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q && setOpen(true)}
          placeholder="Search scholars, posts, jobs, meet-ups…"
          className="w-full pl-9 pr-9 py-2 text-sm rounded-full bg-[color:var(--ayur-cream-2)] border border-[color:var(--ayur-border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--ayur-emerald)]/30"
        />
        {q && (
          <button onClick={() => { setQ(""); setOpen(false); }} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-white">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {open && (q.trim().length > 0) && (
        <div data-testid="global-search-results" className="absolute z-50 top-full mt-2 left-0 right-0 paper-card p-2 max-h-[70vh] overflow-y-auto">
          {loading && <div className="p-4 text-sm text-[color:var(--ayur-mute)] text-center">Searching…</div>}
          {!loading && totalCount === 0 && (
            <div className="p-6 text-sm text-[color:var(--ayur-mute)] text-center">No matches for "{q}"</div>
          )}

          {results.scholars.length > 0 && <>
            <div className="label-eyebrow px-3 pt-2">Scholars</div>
            {results.scholars.map((s) => (
              <button key={s.id} data-testid="search-result-scholar"
                onClick={() => go(`/app/scholars/${s.id}`)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded hover:bg-[color:var(--ayur-cream-2)] text-left">
                <Avatar className="w-8 h-8"><AvatarImage src={resolveImageUrl(s.avatar_url)} />
                  <AvatarFallback className="bg-[color:var(--ayur-badge)] text-[color:var(--ayur-emerald)] text-xs">{initials(s.full_name)}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate inline-flex items-center gap-1">{s.full_name}{s.verified && <VerifiedBadge size={12} />}</div>
                  <div className="text-xs text-[color:var(--ayur-mute)] truncate">{s.college} · Batch {s.batch_year}</div>
                </div>
              </button>
            ))}
          </>}

          {results.gurus.length > 0 && <>
            <div className="label-eyebrow px-3 pt-2">Gurus</div>
            {results.gurus.map((g) => (
              <button key={g.id} onClick={() => go(`/app/scholars/${g.id}`)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded hover:bg-[color:var(--ayur-cream-2)] text-left">
                <Avatar className="w-8 h-8"><AvatarImage src={resolveImageUrl(g.avatar_url)} />
                  <AvatarFallback className="bg-[color:var(--ayur-badge)] text-[color:var(--ayur-emerald)] text-xs">{initials(g.full_name)}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate inline-flex items-center gap-1">{g.full_name}{g.verified && <VerifiedBadge size={12} />}</div>
                  <div className="text-xs text-[color:var(--ayur-mute)] truncate">{g.specialty}</div>
                </div>
              </button>
            ))}
          </>}

          {results.posts.length > 0 && <>
            <div className="label-eyebrow px-3 pt-2">Posts</div>
            {results.posts.map((p) => (
              <button key={p.id} onClick={() => go(`/app/scholars/${p.author?.id}`)}
                className="w-full text-left px-3 py-2 rounded hover:bg-[color:var(--ayur-cream-2)]">
                <div className="text-sm line-clamp-2">{p.content}</div>
                <div className="text-xs text-[color:var(--ayur-mute)]">— {p.author?.full_name}</div>
              </button>
            ))}
          </>}

          {results.jobs.length > 0 && <>
            <div className="label-eyebrow px-3 pt-2">Jobs</div>
            {results.jobs.map((j) => (
              <button key={j.id} onClick={() => go(`/app/jobs`)}
                className="w-full text-left px-3 py-2 rounded hover:bg-[color:var(--ayur-cream-2)]">
                <div className="text-sm font-semibold">{j.title}</div>
                <div className="text-xs text-[color:var(--ayur-mute)]">{j.hospital} · {j.location}</div>
              </button>
            ))}
          </>}

          {results.events.length > 0 && <>
            <div className="label-eyebrow px-3 pt-2">Meet-ups</div>
            {results.events.map((e) => (
              <button key={e.id} onClick={() => go(`/app/meetups`)}
                className="w-full text-left px-3 py-2 rounded hover:bg-[color:var(--ayur-cream-2)]">
                <div className="text-sm font-semibold">{e.title}</div>
                <div className="text-xs text-[color:var(--ayur-mute)]">{e.location} · {new Date(e.date).toLocaleDateString()}</div>
              </button>
            ))}
          </>}
        </div>
      )}
    </div>
  );
}
